import styled from '@xstyled/styled-components';
import React, { FC } from 'react';
import { NavLink } from 'react-router-dom';
import { select } from '~/lib/select';
import { Capsule } from '~/ui/capsules';
import { Chapter, Sequence, TrainingState, useTrainingStore } from '../state';
import { useTrainingUrl } from '../useTrainingUrl';
import { ItemTime } from './ItemTime';
import {
  SequenceStore,
  useSequenceStore
} from '~/training/sequence/SequenceStore';

const selector = (s: TrainingState) =>
  select(s, 'highestUnlockedPosition', 'toggleNav', 'skip');

const sequenceSelector = (s: SequenceStore) => select(s, 'clearBlocker');

const rowLayoutCss = (p: {
  theme: { space: { sm: string; md: string }; radii: { md: string } };
}) => `
  display: grid;
  margin-bottom: ${p.theme.space.md};
  grid-gap: 10px;
  grid-template-columns: auto 1fr auto;
  padding: ${p.theme.space.sm} ${p.theme.space.md};
  border-radius: ${p.theme.radii.md};
  width: 100%;
  box-sizing: border-box;
  text-align: left;
  font: inherit;
  color: inherit;
`;

const Wrapper = styled.divBox`
  display: grid;
  margin-bottom: md;
  grid-gap: 10px;
  grid-template-columns: auto 1fr auto;
  padding: sm md;
  border-radius: md;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
  font: inherit;
  color: ${p => p.theme.colors.black};

  &:hover {
    background: ${p => p.theme.colors.greyLight};
    ${Capsule} {
      background: ${p => p.theme.colors.white};
      color: ${p => p.theme.colors.black};
      svg {
        stroke: ${p => p.theme.colors.black};
        fill: ${p => p.theme.colors.black};
      }
    }
  }

  &.active {
    background: ${p => p.theme.colors.accentDark};
    /* Light brand → black text/icons; dark brand → white (CAT-1261). */
    color: var(--color-on-accent);
    border: 1px solid var(--color-accent-edge);
    ${Capsule} {
      background: ${p => p.theme.colors.white};
      border: 1px solid
        color-mix(in srgb, var(--color-on-accent) 45%, transparent);
      svg {
        stroke: ${p => p.theme.colors.black};
        fill: ${p => p.theme.colors.black};
      }
    }
    ${ItemTime} {
      color: var(--color-on-accent);
    }
    /* Beat DarkStyle .itemStyleForDarkTheme white !important. */
    .itemStyleForDarkTheme {
      color: var(--color-on-accent) !important;
    }
  }
`;

/** CAT-1293 / EL–E117: chapter accordion header must be a real button. */
const ChapterButton = styled.button`
  ${rowLayoutCss}
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${p => p.theme.colors.black};

  &:hover {
    background: ${p => p.theme.colors.greyLight};
    ${Capsule} {
      background: ${p => p.theme.colors.white};
      color: ${p => p.theme.colors.black};
      svg {
        stroke: ${p => p.theme.colors.black};
        fill: ${p => p.theme.colors.black};
      }
    }
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid var(--color-focus-ring);
    outline-offset: 2px;
  }
`;

/** Heading wrapper so the button is the only child (APG accordion). */
const ChapterHeading = styled.h3`
  margin: 0;
  font: inherit;
  font-weight: inherit;
`;

export const ItemContainer: FC<{
  item: Chapter | Sequence;
  onClick: () => void;
  ariaExpanded: boolean;
  /** id of the sequences panel controlled by a chapter header (CAT-1293). */
  panelId?: string;
}> = ({ item, children, ariaExpanded, onClick, panelId }) => {
  const url = useTrainingUrl();
  const { highestUnlockedPosition, toggleNav, skip } =
    useTrainingStore(selector);
  const { clearBlocker } = useSequenceStore(sequenceSelector);

  const isUnlocked = item.position <= highestUnlockedPosition || skip;

  if (isUnlocked) {
    if (item.kind === 'chapter') {
      return (
        <ChapterHeading>
          <ChapterButton
            type="button"
            aria-expanded={ariaExpanded}
            aria-controls={panelId}
            onClick={onClick}
          >
            {children}
          </ChapterButton>
        </ChapterHeading>
      );
    }

    return (
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      <Wrapper
        ml="lg"
        as={NavLink}
        to={`${url}/${item.id}?disableAutoPlay=true`}
        onClick={() => {
          clearBlocker();
          setTimeout(() => toggleNav(false), 100);
        }}
        activeClassName="active"
      >
        {children}
      </Wrapper>
    );
  }

  // CAT-1294 / EL–E118: locked rows are not interactive — no tabindex.
  return <Wrapper ml={item.kind === 'sequence' ? 'lg' : 0}>{children}</Wrapper>;
};
